"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { getActiveWaveIdFromUrl } from "@/helpers/navigation.helpers";
import { useAuthenticatedContent } from "../../../hooks/useAuthenticatedContent";
import useDeviceInfo from "../../../hooks/useDeviceInfo";
import ConnectWallet from "../../common/ConnectWallet";
import HeaderUserConnect from "../../header/user/HeaderUserConnect";
import UserSetUpProfileCta from "../../user/utils/set-up-profile/UserSetUpProfileCta";
import WavesDesktop from "../WavesDesktop";
import WavesMobile from "../WavesMobile";
import PublicWaveShell from "../public/PublicWaveShell";
import WaveScreenMessage from "../WaveScreenMessage";
function getConnectPrompt(contentState: ReturnType<typeof useAuthenticatedContent>["contentState"]): ReactNode {
  switch (contentState) {
    case "needs-profile":
      return <>
          <h1 className="tw-text-xl tw-font-bold">
            You need to set up a profile to continue.
          </h1>
          <UserSetUpProfileCta />
        </>;
    case "not-available":
      return <h1 className="tw-text-xl tw-font-bold">
          This content is not available.
        </h1>;
    case "loading":
    case "measuring":
    case "not-authenticated":
    case "ready":
    default:
      return null;
  }
}
function getNotAuthenticatedContent({
  activeWaveId,
  containerClassName,
  isApp,
  isMobileDevice
}: {
  readonly activeWaveId: string | null;
  readonly containerClassName: string;
  readonly isApp: boolean;
  readonly isMobileDevice: boolean;
}): ReactNode {
  if (isApp || isMobileDevice && activeWaveId === null) {
    return <ConnectWallet />;
  }
  return <div className="tw-flex-1" id="waves-content">
      <WavesDesktop allowDropOverlay={false} allowRightSidebar={false} showLeftSidebar={true}>
        <div className={containerClassName}>
          {activeWaveId === null ? <WaveScreenMessage title="Select a Wave" description="Connect your wallet to access waves and join the conversation." action={<HeaderUserConnect label="Connect Wallet" />} /> : <PublicWaveShell waveId={activeWaveId} />}
        </div>
      </WavesDesktop>
    </div>;
}

// Main layout content that uses the Layout context
function WavesLayoutContent(t0) {
  const $ = _c(18);
  const {
    children
  } = t0;
  const {
    contentState
  } = useAuthenticatedContent();
  const {
    isApp,
    isMobileDevice
  } = useDeviceInfo();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let content;
  if ($[0] !== children || $[1] !== contentState || $[2] !== isApp || $[3] !== isMobileDevice || $[4] !== pathname || $[5] !== searchParams) {
    const activeWaveId = getActiveWaveIdFromUrl({
      pathname,
      searchParams
    });
    let t1;
    if ($[7] !== contentState) {
      t1 = getConnectPrompt(contentState);
      $[7] = contentState;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    const connectPrompt = t1;
    if (contentState === "ready") {
      const Component = isApp ? WavesMobile : WavesDesktop;
      let t2;
      if ($[9] !== children) {
        t2 = <div className="tw-relative tw-flex tw-flex-col tw-flex-1 tailwind-scope">{children}</div>;
        $[9] = children;
        $[10] = t2;
      } else {
        t2 = $[10];
      }
      let t3;
      if ($[11] !== Component || $[12] !== t2) {
        t3 = <div className="tw-flex-1" id="waves-content"><Component>{t2}</Component></div>;
        $[11] = Component;
        $[12] = t2;
        $[13] = t3;
      } else {
        t3 = $[13];
      }
      content = t3;
    } else {
      if (contentState === "not-authenticated") {
        content = getNotAuthenticatedContent({
          activeWaveId,
          containerClassName: "tw-relative tw-flex tw-flex-col tw-flex-1 tailwind-scope",
          isApp,
          isMobileDevice
        });
      } else {
        let t2;
        if ($[14] !== connectPrompt) {
          t2 = connectPrompt === null ? null : <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-px-6"><div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-text-center">{connectPrompt}</div></div>;
          $[14] = connectPrompt;
          $[15] = t2;
        } else {
          t2 = $[15];
        }
        content = t2;
      }
    }
    $[0] = children;
    $[1] = contentState;
    $[2] = isApp;
    $[3] = isMobileDevice;
    $[4] = pathname;
    $[5] = searchParams;
    $[6] = content;
  } else {
    content = $[6];
  }
  let t1;
  if ($[16] !== content) {
    t1 = <div className="tailwind-scope tw-flex tw-flex-col tw-overflow-hidden tw-bg-black">{content}</div>;
    $[16] = content;
    $[17] = t1;
  } else {
    t1 = $[17];
  }
  return t1;
}
export default function WavesLayout(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <WavesLayoutContent>{children}</WavesLayoutContent>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}